import { useEffect, useState } from "react";
import { Plus, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import ConsentModal from "@/components/patients/ConsentModal";
import { PATIENTS } from "@/constants/testIds";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) +
    " \u00b7 " + d.toTimeString().slice(0, 5);
}

/** Lista de consentimientos informados firmados por el paciente. */
export default function ConsentsList({ patientId, patientName }) {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/consents`);
      setConsents(data);
    } catch (err) {
      toast({ title: "Error al cargar consentimientos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Consentimientos firmados</h3>
        <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={() => setModalOpen(true)} data-testid={PATIENTS.consentAddButton}>
          <Plus className="w-3.5 h-3.5" /> Nuevo consentimiento
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Cargando...</p>
      ) : consents.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={PATIENTS.consentEmptyState}>
          <FileSignature className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Aun no hay consentimientos firmados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {consents.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-xl p-4 space-y-2" data-testid={`${PATIENTS.consentItemPrefix}${c.id}`}>
              <p className="font-medium text-foreground text-sm">{c.title}</p>
              <p className="text-xs text-muted-foreground">Firmado por {c.signed_by_name} &middot; {formatDate(c.signed_at)}</p>
              {c.consent_text && <p className="text-xs text-muted-foreground line-clamp-2">{c.consent_text}</p>}
              <img src={getFileUrl(c.signature_url)} alt="firma" className="h-14 border border-border rounded bg-[#FBFAF7] object-contain" />
            </div>
          ))}
        </div>
      )}

      <ConsentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        patientId={patientId}
        patientName={patientName}
        onCreated={() => { toast({ title: "Consentimiento guardado" }); load(); }}
      />
    </div>
  );
}
